import * as React from 'react';
import { ChangeEvent } from 'react';
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import { IconButton, Pagination, PaginationItem, Stack, styled, Tooltip } from '@mui/material';

const PAGE_SIBLING_COUNT = 5;
const JUMP_STEP = 10;

type CustomPaginationProps = {
  page: number;
  count: number;
  onChangePage: (value: number) => void;
};

export const CustomPagination = ({ page, count, onChangePage }: CustomPaginationProps) => {
  const safeCount = Math.max(1, count);
  const safePage = Math.min(Math.max(1, page), safeCount);

  const handlePageChange = (_e: ChangeEvent<unknown>, value: number) => {
    onChangePage(value);
  };

  const jumpBack = () => onChangePage(Math.max(1, safePage - JUMP_STEP));
  const jumpForward = () => onChangePage(Math.min(safeCount, safePage + JUMP_STEP));

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      sx={{
        width: '100%',
        minHeight: 48,
        // Stable footprint: ellipsis/page count changes won’t yank the whole bar sideways
        '& .MuiPagination-root': {
          margin: 0
        },
        '& .MuiPagination-ul': {
          flexWrap: 'nowrap',
          justifyContent: 'center',
          gap: '2px',
          minWidth: { xs: 280, sm: 520, md: 600 },
          maxWidth: '100%'
        }
      }}>
      <Tooltip title={`Back ${JUMP_STEP} pages`}>
        <span>
          <IconButton
            aria-label={`Go back ${JUMP_STEP} pages`}
            onClick={jumpBack}
            disabled={safePage <= 1}
            size="small"
            sx={{
              color: '#226AAA',
              flexShrink: 0,
              width: 40,
              height: 40
            }}>
            <KeyboardDoubleArrowLeft />
          </IconButton>
        </span>
      </Tooltip>

      <Pagination
        color="primary"
        count={safeCount}
        page={safePage}
        onChange={handlePageChange}
        siblingCount={PAGE_SIBLING_COUNT}
        boundaryCount={1}
        showFirstButton
        showLastButton
        renderItem={(item) => <CustomPaginationItem {...item} />}
      />

      <Tooltip title={`Forward ${JUMP_STEP} pages`}>
        <span>
          <IconButton
            aria-label={`Go forward ${JUMP_STEP} pages`}
            onClick={jumpForward}
            disabled={safePage >= safeCount}
            size="small"
            sx={{
              color: '#226AAA',
              flexShrink: 0,
              width: 40,
              height: 40
            }}>
            <KeyboardDoubleArrowRight />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

const CustomPaginationItem = styled(PaginationItem)(() => ({
  color: '#226AAA',
  fontWeight: 400,
  minWidth: 40,
  height: 40,
  maxWidth: 48,
  margin: 0,
  padding: '0 2px',
  fontSize: '16px',
  flexShrink: 0,
  '&.Mui-selected': {
    color: '#FFF !important',
    backgroundColor: '#226AAA !important'
  },
  '&.MuiPaginationItem-previousNext, &.MuiPaginationItem-firstLast': {
    margin: 0,
    minWidth: 40
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.35rem'
  }
}));
